import type { IHSL, IHSV, IRGB } from '@/typescript/interfaces/colors';

function hsvToHsl(hsvToTransform: IHSV): IHSL {
    const { h } = hsvToTransform;
    const s = hsvToTransform.s / 100;
    const v = hsvToTransform.v / 100;
    const tranformedHsl = { h, s: 0, l: 0 };
    tranformedHsl.l = v * (1 - s / 2);
    if (tranformedHsl.l > 0 && tranformedHsl.l < 1) {
        tranformedHsl.s = Math.round(((v - tranformedHsl.l) / Math.min(tranformedHsl.l, 1 - tranformedHsl.l)) * 100);
    }
    tranformedHsl.l = Math.round(tranformedHsl.l * 100);

    return tranformedHsl;
}

function hslToRgb(hslToTransform: IHSL): IRGB {
    const { h } = hslToTransform;
    const s = hslToTransform.s / 100;
    const l = hslToTransform.l / 100;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number, k = (n + h / 30) % 12) => l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);

    return { r: Math.round(f(0) * 255), g: Math.round(f(8) * 255), b: Math.round(f(4) * 255) };
}

function rgbToHexa(rgbToTransform: IRGB, alpha?: number): string {
    const { r, g, b } = rgbToTransform;
    let hexAlpha = '';
    if (alpha !== undefined) {
        hexAlpha = percentToHex(alpha);
    }
    const hexR = r.toString(16).padStart(2, '0');
    const hexG = g.toString(16).padStart(2, '0');
    const hexB = b.toString(16).padStart(2, '0');
    return `#${hexR}${hexG}${hexB}${hexAlpha}`;
}

function hexaToRgb(hexaToTransform: string): IRGB | null {
    const matches = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})/i.exec(hexaToTransform);
    if (!matches || matches?.length < 4) {
        return null;
    }
    return {
        r: parseInt(matches[1], 16),
        g: parseInt(matches[2], 16),
        b: parseInt(matches[3], 16),
    };
}

function rgbToHsv(rgbToTransform: IRGB): IHSV {
    const r = rgbToTransform.r / 255;
    const g = rgbToTransform.g / 255;
    const b = rgbToTransform.b / 255;
    const v = Math.max(r, g, b);
    const c = v - Math.min(r, g, b);
    // eslint-disable-next-line no-nested-ternary
    const h = c && (v === r ? (g - b) / c : v === g ? 2 + (b - r) / c : 4 + (r - g) / c);
    return { h: Math.round(60 * (h < 0 ? h + 6 : h)), s: Math.round((v && c / v) * 100), v: Math.round(v * 100) };
}

function hexToPercent(hex: string) {
    return Math.round((parseInt(hex, 16) * 100) / 255);
}

function percentToHex(percent: number) {
    return Math.round(percent * 2.55).toString(16);
}

export { hsvToHsl, hslToRgb, rgbToHexa, hexaToRgb, rgbToHsv, hexToPercent, percentToHex };
